import { makeAutoObservable } from 'mobx';
import { IDragDroppedFile, IOutlookItemInfo } from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';

export interface IDragDropTarget {
	getUniqueDragDropId(): string;
	getElementRef(): HTMLElement | null;
	notifyDragEnter(): void;
	notifyDragLeave(): void;
	notifyDrop: (files: IDragDroppedFile[]) => Promise<void>;
}

/**
 * Manages drag and drop from Outlook emails/attachments to folder locations.
 * Uses Office.js drag events and element positioning to determine drop targets.
 */
export class OutlookDragDropManager {
	private folders: Map<string, IDragDropTarget> = new Map();
	public currentHovered?: IDragDropTarget = undefined;

	constructor() {
		makeAutoObservable(this);

		window.onmouseleave = () => {
			if (this.currentHovered) {
				this.currentHovered.notifyDragLeave();
				this.currentHovered = undefined;
			}
		}
	}

	/**
	 * Register a folder as a drop target
	 */
	public registerFolder(target: IDragDropTarget): void {
		this.folders.set(target.getUniqueDragDropId(), target);
	}

	/**
	 * Unregister a folder from being a drop target
	 */
	public unregisterFolder(target: IDragDropTarget): void {
		this.folders.delete(target.getUniqueDragDropId());
	}

	/**
	 * Handle Office.js dragover event - determine which folder is under the cursor
	 */
	public handleDragOver(x: number, y: number): void {

		let originalDragTarget = this.currentHovered;

		//Find which folder is under the cursor
		let elementUnderCursor = document.elementFromPoint(x, y);
		if (elementUnderCursor) {

			//Go through the folders in reverse, so we're getting the deepest nested first
			const folderArray = Array.from(this.folders.values()).reverse();

			for (const folder of folderArray) {
				let elementRef = folder.getElementRef();
				if (elementRef && elementRef.contains(elementUnderCursor)) {
					this.currentHovered = folder;
					break;
				}
			}
		}

		if (this.currentHovered !== originalDragTarget) {
			//Notify drag leave on original target
			originalDragTarget?.notifyDragLeave();
			this.currentHovered?.notifyDragEnter();
		}
	}

	/**
	 * Handle Office.js drop event - file items to the hovered folder
	 */
	public handleDrop(selectedItems: IOutlookItemInfo[], files: IDragDroppedFile[]): void {

		this.currentHovered?.notifyDrop(files);
		this.currentHovered?.notifyDragLeave();
		this.currentHovered = undefined;
	}

}
